import { parseDate as chronoParseDate } from "chrono-node";
import { DateTime } from "luxon";
import { templateVariable } from "./parsing";

/**
 * This is a helper class that receives a string reminder suggestion template,
 * e.g. `{{TIME}} on February {{NUMBER}}`, and handles updating that
 * template in response to provided user time and number values. The
 * final result is a string that *might* match the user input. For example,
 * if a user provided a 9 am time value and a 15 number value as part of their input,
 * and we had `Suggestion` templates of the form `{{TIME}} on January {{NUMBER}}`
 * and `{{TIME}} on February {{NUMBER}}`, they would both be updated in response
 * to the user input like `9 am on January 15` and `9 am on February 15`. Then we
 * pass these strings to our preexisting fuzzy matching function to see if either
 * of these suggestion strings matches the user input. This approach makes it easier
 * for us to handle typos/misspellings.
 */
export class DateTimeSuggestion {
  // Note that chrono-node's ability to parse times across DST boundaries seems to be
  // unreliable. As such, we choose to only parse the non-time portion of a suggestion using
  // chrono-node and then we manually set the time on the resulting date object.
  static parseDate(content: string, now: DateTime) {
    return DateTime.fromJSDate(
      chronoParseDate(content, { instant: now.toJSDate(), timezone: now.offset }, { forwardDate: true }),
      { zone: now.zone },
    );
  }

  static defaultGetDateFn(setDefaultTimeForDateSuggestion: (date: DateTime<true>) => DateTime<true> | DateTime<false>) {
    return function (
      this: DateTimeSuggestion,
      /** The timezone will be extracted from the `now` DateTime value provided */
      now: DateTime<true>,
    ): DateTime<true> | DateTime<false> | null {
      if (this.content === null) return null;

      let date: DateTime;

      if (this.valueType === "date") {
        const parsedDate = DateTimeSuggestion.parseDate(this.content, now);
        if (!parsedDate.isValid) return parsedDate;
        date = setDefaultTimeForDateSuggestion(parsedDate);
      } else {
        date = DateTimeSuggestion.parseDate(this.content, now);
      }

      // We intentionally return invalid dates. The suggestion UI will indicate if a date resulting from
      // user input is invalid and ignore the suggestion if it is.
      return date;
    };
  }

  id: string;

  template: string;
  /**
   * This is the WIP string that we'll eventually
   * display to the user.
   */
  content: string | null;
  contentVariables: DateTimeSuggestionVariables = {};
  /**
   * The type of date that the template is interpreted to mean
   */
  valueType: "datetime" | "date" | "week" | "month" | "year" | null;
  /**
   * Additional keywords that we can use for fuzzy
   * matching.
   */
  keywords: string[] = [];

  disabled: boolean = false;

  /** Can return invalid dates */
  getDate: (
    this: DateTimeSuggestion,
    /** The timezone will be extracted from the `now` DateTime value provided */
    now: DateTime<true>,
  ) => DateTime<true> | DateTime<false> | null;

  finalizeUpdates: (this: DateTimeSuggestion, now: DateTime<true>) => void = () => {
    if (!this.content || templateVariable.toRegExp().test(this.content)) {
      this.disabled = true;
    }
  };

  /**
   * A function that is used to update the `content` value
   * with template variable values. The updateFn can
   * choose to set the `content` to `null` to indicate
   * that, for whatever reason, this suggestion should not
   * be shown to the user.
   */
  protected updateFn(
    this: DateTimeSuggestion,
    args: {
      /**
       * The WIP string value for this suggestion.
       * For example, "this Friday at {{TIME}}".
       */
      content: string;
      /** a token such as `"{{DATE}}"` or `"{{TIME}}"` */
      token: string;
      /** the value of this token such as `"02/28/2023"` or `"9 pm"` */
      value: string;
    },
  ): void {
    if (this.content === null) return;
    // An updateFn must be provided as a constructor argument if the suggestion
    // uses a template variable.
    throw new Error("[DateTimeSuggestion] [updateFn] not implemented");
  }

  constructor(args: {
    id: string;
    template: string;
    valueType: "datetime" | "date" | "week" | "month" | "year" | null;
    /** Can return invalid dates */
    getDate: (this: DateTimeSuggestion, now: DateTime<true>) => DateTime<true> | DateTime<false> | null;
    /**
     * A function that is used to update the `content` value
     * with template variable values. The updateFn can
     * choose to set the `content` to `null` to indicate
     * that, for whatever reason, this suggestion should not
     * be shown to the user.
     */
    updateFn?: DateTimeSuggestion["updateFn"];

    /**
     * A function that is called after all calls to `update` have been made.
     * Useful for setting default values for content variables or for disabling
     * the suggestion.
     */
    finalizeUpdates?: (this: DateTimeSuggestion, now: DateTime<true>) => void;
    /**
     * keywords that should be used to help match this suggestion
     * to the user input.
     */
    keywords?: string[];
  }) {
    this.id = args.id;
    this.template = args.template;
    this.content = args.template;
    this.valueType = args.valueType;
    this.getDate = args.getDate.bind(this);

    if (args.finalizeUpdates) this.finalizeUpdates = args.finalizeUpdates.bind(this);
    if (args.updateFn) this.updateFn = args.updateFn.bind(this);
    if (args.keywords) this.keywords = args.keywords.slice();
  }

  /** Mutates object */
  update(token: string, value: string) {
    if (this.content === null) return;
    this.updateFn({ content: this.content, token, value });
  }
}

export interface DateTimeSuggestionVariables {
  amount?: number;
  date?: string;
  time?: { hours: number; minutes: number };
  dayOfMonth?: number;
}
